import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import type { TextOnPathProps } from '../../TextOnPath.types';
import type { TextOnPathSvgProps } from './TextOnPathSvg';
import classNames from 'clsx';
import { PauseSmall, PlaySmall } from '@wix/wix-ui-icons-common';
import styles from '../TextOnPath.scss';
import { ViewerTestIds } from '../../constants';

type PauseReason = 'click' | 'hover' | null;

type TextOnPathClientProps = Pick<TextOnPathSvgProps, 'isAnimated' | 'link'> &
  Pick<TextOnPathProps, 'translations'> & {
    pauseOnHover: boolean;
    children: React.ReactNode;
    defaultPaused?: boolean;
  };

export function TextOnPathA11yWrapper({
  translations,
  pauseOnHover,
  children,
  isAnimated,
  defaultPaused = false,
}: TextOnPathClientProps) {
  const [isAnimationPaused, setIsAnimationPaused] = useState(defaultPaused);

  const pauseReasonRef = useRef<PauseReason>(null);

  const svgRef = (wrapperElement?: HTMLDivElement | null) => {
    return wrapperElement?.querySelector('[data-svg-ref]') as SVGSVGElement;
  };

  const wrapperDivRef = useRef<HTMLDivElement>(null);

  const wrapperRefCallback = useCallback(
    (element: HTMLDivElement | null) => {
      // Update the ref
      (wrapperDivRef as React.MutableRefObject<HTMLDivElement | null>).current =
        element;

      if (element && isAnimated && defaultPaused) {
        // Use 'click' as the pause reason so the first user click resumes playback
        const svg = svgRef(element);
        if (svg) {
          try {
            svg.pauseAnimations();
            pauseReasonRef.current = 'click';
            setIsAnimationPaused(true);
          } catch (error) {
            // Fail silently if animation methods are not supported
          }
        }
      }
    },
    [isAnimated, defaultPaused],
  );

  const pauseAnimation = useCallback((reason: PauseReason) => {
    const svg = svgRef(wrapperDivRef.current);
    if (!svg) {
      return;
    }

    try {
      svg.pauseAnimations();
      pauseReasonRef.current = reason;
      setIsAnimationPaused(true);
    } catch (error) {
      // Fail silently if animation methods are not supported
    }
  }, []);

  const resumeAnimation = useCallback((reason: PauseReason) => {
    const svg = svgRef(wrapperDivRef.current);
    if (!svg || pauseReasonRef.current !== reason) {
      return;
    }

    try {
      svg.unpauseAnimations();
      pauseReasonRef.current = null;
      setIsAnimationPaused(false);
    } catch (error) {
      // Fail silently if animation methods are not supported
    }
  }, []);

  const mouseEnterHandler = () => {
    if (!pauseReasonRef.current) {
      pauseAnimation('hover');
    }
  };

  const mouseLeaveHandler = () => {
    resumeAnimation('hover');
  };

  const handleClick = () => {
    if (!isAnimated) {
      return;
    }

    if (pauseReasonRef.current === 'click') {
      resumeAnimation('click');
    } else {
      pauseAnimation('click');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Space') {
      handleClick();
      e.preventDefault();
    }
  };

  return (
    <div
      onMouseEnter={pauseOnHover ? mouseEnterHandler : undefined}
      onMouseLeave={pauseOnHover ? mouseLeaveHandler : undefined}
      onClick={handleClick}
      ref={wrapperRefCallback}
      data-testid={ViewerTestIds.a11yRoot}
      className={styles.textOnPathA11yWrapper}
    >
      {children}
      {isAnimated && (
        <button
          className={classNames(styles.playButton, styles.visuallyHidden)}
          onKeyDown={handleKeyDown}
          aria-label={
            isAnimationPaused ? translations.playLabel : translations.pauseLabel
          }
          aria-pressed={isAnimationPaused}
        >
          {isAnimationPaused ? (
            <>
              <span>{translations.playLabel}</span>
              <PlaySmall />
            </>
          ) : (
            <>
              <span>{translations.pauseLabel}</span>
              <PauseSmall />
            </>
          )}
        </button>
      )}
    </div>
  );
}
